import { Box, ButtonBase, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { getHistory } from "../../../services/orders/history";
import { Block, Skeleton } from "../../shared";
import { HistoryRow } from "../history/HistoryRow";

interface CardHistoryBlockProps {
  cardId: string;
  /** Сколько последних операций показывать. По умолчанию 8. */
  limit?: number;
}

/**
 * Блок «История операций по карте» на экране `/cards/:id`.
 *
 * Использует единый эндпойнт `/me/orders` с новым фильтром `cardId=<...>`:
 * это позволяет переиспользовать все существующие компоненты (`HistoryRow`,
 * helpers) — тот же формат `HistoryOrder`, те же статусы и суммы. Никакой
 * отдельной transactions-инфраструктуры пока не нужно.
 *
 * Что сюда попадает:
 *   - `CARD_ISSUE` (выпуск карты, списание баланса),
 *   - в будущем — `CARD_TOPUP` (пополнение карты, spend/refund).
 *
 * SBP-платежи, `EXCHANGE`, депозиты/выводы сюда не попадают — они не
 * привязаны к `card_id`. Всё это лежит в общей истории `/history`.
 */
export function CardHistoryBlock({ cardId, limit = 8 }: CardHistoryBlockProps) {
  const { t } = useTranslation("cards");
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["card-history", cardId, limit],
    queryFn: () => getHistory({ page: 1, pageSize: limit, cardId }),
    staleTime: 10_000,
  });

  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <Block sx={{ padding: "16px 18px" }}>
        <Skeleton
          variant="rounded"
          width={160}
          height={22}
          sx={{ borderRadius: "8px", marginBottom: "14px" }}
        />
        <Skeleton
          variant="rounded"
          height={48}
          sx={{ borderRadius: "12px", marginBottom: "8px" }}
        />
        <Skeleton
          variant="rounded"
          height={48}
          sx={{ borderRadius: "12px", marginBottom: "8px" }}
        />
        <Skeleton
          variant="rounded"
          height={48}
          sx={{ borderRadius: "12px" }}
        />
      </Block>
    );
  }

  return (
    <Block sx={{ padding: "6px 12px 6px" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingY: "12px",
          paddingX: "6px",
        }}
      >
        <Typography sx={{ fontSize: "17px", fontWeight: 600 }}>
          {t("history.title")}
        </Typography>
        {items.length > 0 && (
          <ButtonBase
            onClick={() => navigate("/history")}
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: "primary.main",
              padding: "4px 8px",
              borderRadius: "8px",
              "&:active": { opacity: 0.7 },
            }}
          >
            {t("history.view_all")}
          </ButtonBase>
        )}
      </Box>

      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <Box>
          {items.map((order, idx) => (
            <Fragment key={order.id}>
              {idx > 0 && (
                <Box
                  sx={{
                    height: "1px",
                    bgcolor: "divider",
                    marginLeft: "48px",
                  }}
                />
              )}
              <HistoryRow order={order} />
            </Fragment>
          ))}
        </Box>
      )}
    </Block>
  );
}

function EmptyState() {
  const { t } = useTranslation("cards");
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        paddingY: "28px",
        paddingX: "16px",
      }}
    >
      <Box
        sx={(theme) => ({
          width: 56,
          height: 56,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.05)",
          color: theme.palette.primary.main,
        })}
      >
        <Icon
          icon="solar:clock-square-bold-duotone"
          width={28}
          height={28}
          color="currentColor"
        />
      </Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, textAlign: "center" }}>
        {t("history.empty_title")}
      </Typography>
      <Typography
        sx={{
          fontSize: "12px",
          color: "text.secondary",
          textAlign: "center",
          maxWidth: 260,
          lineHeight: 1.45,
        }}
      >
        {t("history.empty_subtitle")}
      </Typography>
    </Box>
  );
}
